import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Delete, Save } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  ButtonGroup,
  FormControl,
  LinearProgress,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import buildings from 'Api/buildings';

import { usePortfolioContext } from 'Components/Portfolios/context';

const EditTerritory = ({ id, cancel }) => {
  const [territory, setTerritory] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const { handleSubmit, register, setValue, formState } = useForm();
  const { fetchPortfoliosAndSites } = usePortfolioContext();

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      fetchTerritoryById(id);
    }
  }, []);

  const fetchTerritoryById = async (id) => {
    const data = await buildings.getTerritory(id);
    setTerritory(data?.data?.data ?? null);
    setValue('name', data?.data?.data?.name ?? '');
    setValue('regionId', data?.data?.data?.regionId ?? '');
    setIsLoading(false);
  };

  const onSubmit = (data) => {
    setIsLoading(true);
    buildings
      .updateTerritory(territory.id, data)
      .then(() => {
        fetchPortfoliosAndSites();
        cancel();
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDelete = () => {
    setIsLoading(true);
    buildings
      .deleteTerritory(territory.id)
      .then(() => {
        fetchPortfoliosAndSites();
        cancel();
      })
      .catch((err) => {
        setError(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderForm = () => {
    if (territory && !isLoading) {
      return (
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth>
            <TextField
              id="territory-id"
              fullWidth
              required
              label="Territory name"
              margin="normal"
              {...register('name')}
            />
          </FormControl>
          <FormControl fullWidth sx={{ marginTop: 1 }}>
            <TextField
              id="region-select"
              label="Region"
              fullWidth
              select
              required
              inputProps={register('regionId')}
              defaultValue={territory.regionId}
            >
              {territory.regions.map((r) => (
                <MenuItem key={r.id} value={r.id}>
                  {r.name}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          {territory?.sites?.length > 0 && (
            <Alert severity="warning" sx={{ marginTop: 1 }}>
              You cannot delete the territory because it has the following sites
              associated with it:{' '}
              {territory.sites.map((t) => t.name).join(', ')}
            </Alert>
          )}
          <Stack direction="row" justifyContent="space-between" mt={2}>
            <Button
              variant="contained"
              startIcon={<Save />}
              disabled={!formState.isDirty}
              type="submit"
            >
              Save
            </Button>
            <ButtonGroup>
              <Button
                startIcon={<Delete />}
                color="error"
                disabled={territory?.sites?.length > 0}
                onClick={handleDelete}
              >
                Delete
              </Button>
              <Button onClick={cancel}>Cancel</Button>
            </ButtonGroup>
          </Stack>
        </form>
      );
    }
    return null;
  };

  return (
    <Stack p={2} spacing={2}>
      {isLoading && <LinearProgress />}
      <Typography variant="h5">Edit territory {id}</Typography>
      <Box py={2}>{renderForm()}</Box>
      {!!error && (
        <Alert severity="error">Error when updating the territory</Alert>
      )}
    </Stack>
  );
};

export default EditTerritory;
